import { ChevronRight, EmojiEvents, Flight, Home } from '@mui/icons-material';
import { Box, FormControlLabel, Switch, TableRow, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  capitalizar,
  formatClubTeamName,
  formatFechaPartido,
  ordenarLista,
  ordenarPorCategoria,
} from '../../utils/UtilesComun';
import { temporadaActual } from '../../utils/UtilesContexto';
import { BadgeResultadoPartidoApp, TableCellApp, TableModuleApp, TypographyApp } from '../custom/CustomComponents';

const SEPARADOR_VERTICAL = '1px solid #e2e8f0';
const SEPARADOR_CATEGORIA = '2.5px solid #94a3b8';

/**
 * Obtiene el nombre capitalizado de la división de la liga de un equipo.
 */
const obtenerDivisionTexto = (equipo) => {
  if (equipo?.division) {
    return capitalizar(equipo.division);
  }
  if (equipo?.nombreLiga) {
    const sinGrupo = equipo.nombreLiga.replace(/\s+grupo\s+.*$/i, '').trim();
    return capitalizar(sinGrupo || equipo.nombreLiga);
  }
  return '';
};

const COLUMNAS_EQUIPOS_CLUB = [
  {
    field: 'nombreEquipo',
    label: 'Equipo',
    width: '12%',
    tooltip: 'Equipo y categoría',
    sx: { borderRight: SEPARADOR_VERTICAL },
  },
  {
    field: 'proximoPartido',
    label: 'Próximo',
    width: '35%',
    sortable: false,
    tooltip: 'Próximo rival programado',
    sx: { borderRight: SEPARADOR_VERTICAL },
  },
  {
    field: 'ultimoPartido',
    label: 'Último',
    width: '28%',
    sortable: false,
    tooltip: 'Resultado del último partido jugado',
    sx: { borderRight: SEPARADOR_VERTICAL },
  },
  {
    field: 'posicion',
    label: 'Liga',
    width: '15.5%',
    tooltip: 'División y posición actual en liga (clic para ver clasificación)',
    sx: { borderRight: SEPARADOR_VERTICAL },
  },
  {
    field: 'partidosSinValorar',
    label: 'Valoración',
    width: '9.5%',
    align: 'center',
    tooltip: 'Partidos disputados pendientes de evaluar',
  },
];

/**
 * Fila individual del cuadro de mando de un equipo del club.
 */
const FilaEquipoClub = ({ equipo, columns, esNuevaCategoria = false }) => {
  const navigate = useNavigate();

  return (
    <TableRow
      hover
      onClick={() => navigate(`/equipo/${equipo.idEquipo}`)}
      sx={{
        cursor: 'pointer',
        ...(esNuevaCategoria && {
          '& > td': {
            borderTop: SEPARADOR_CATEGORIA,
          },
        }),
      }}
    >
      {columns.map((col, idx) => {
        const isLastCol = idx === columns.length - 1;
        const borderRight = isLastCol ? 'none' : SEPARADOR_VERTICAL;
        const borderTop = esNuevaCategoria ? SEPARADOR_CATEGORIA : undefined;

        // 1. Nombre del equipo y categoría
        if (col.field === 'nombreEquipo') {
          const nombreFormateado = formatClubTeamName(equipo.nombreEquipo, equipo.categoria);
          const esNoCompite = !equipo.enCompeticion;
          const tooltipTexto = esNoCompite
            ? `${nombreFormateado} * (Sin competición oficial esta temporada)`
            : nombreFormateado;

          return (
            <TableCellApp
              key={col.field}
              sx={{
                py: 0.75,
                pl: 1.25,
                pr: 0.75,
                overflow: 'hidden',
                borderRight,
                ...(borderTop && { borderTop }),
              }}
            >
              <Tooltip title={tooltipTexto} arrow placement="top">
                <TypographyApp
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.82rem',
                    color: 'text.primary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.4,
                    '&:hover': {
                      color: 'primary.main',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {nombreFormateado}
                  </Box>
                  {esNoCompite && (
                    <Box
                      component="span"
                      sx={{
                        color: 'warning.dark',
                        fontWeight: 700,
                        fontSize: '0.90rem',
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                    >
                      *
                    </Box>
                  )}
                </TypographyApp>
              </Tooltip>
            </TableCellApp>
          );
        }

        // 2. Próximo rival programado
        if (col.field === 'proximoPartido') {
          const proximo = equipo.proximoPartido;
          if (!proximo) {
            return (
              <TableCellApp
                key={col.field}
                sx={{
                  py: 0.75,
                  px: 0.75,
                  overflow: 'hidden',
                  borderRight,
                  ...(borderTop && { borderTop }),
                }}
              >
                <TypographyApp sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>-</TypographyApp>
              </TableCellApp>
            );
          }

          const fechaInfo = formatFechaPartido(proximo.fecha);
          const tieneFecha = Boolean(fechaInfo && fechaInfo.fecha && fechaInfo.fecha !== '-');
          const condicion = proximo.esLocal ? 'Casa' : 'Fuera';
          const jornadaTxt = proximo.jornada ? ` · J${proximo.jornada}` : '';
          const fechaCompletaTxt = fechaInfo.completa ? ` · ${fechaInfo.completa}` : '';
          const tooltipTitle = `${condicion} vs ${capitalizar(proximo.rival || '')}${jornadaTxt}${fechaCompletaTxt}`;

          return (
            <TableCellApp
              key={col.field}
              sx={{
                py: 0.75,
                px: 0.75,
                overflow: 'hidden',
                borderRight,
                ...(borderTop && { borderTop }),
              }}
            >
              <Tooltip title={tooltipTitle} arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65, overflow: 'hidden' }}>
                  {tieneFecha ? (
                    <Box
                      onClick={(e) => {
                        if (proximo.idPartido) {
                          e.stopPropagation();
                          navigate(`/partido/${proximo.idPartido}`);
                        }
                      }}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.35,
                        px: 0.65,
                        py: 0.2,
                        borderRadius: '4px',
                        backgroundColor: '#f1f5f9',
                        color: '#334155',
                        border: '1px solid #cbd5e1',
                        fontSize: '0.74rem',
                        fontWeight: 600,
                        flexShrink: 0,
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                        cursor: proximo.idPartido ? 'pointer' : 'default',
                        transition: 'all 0.15s ease-in-out',
                        '&:hover': proximo.idPartido
                          ? {
                              backgroundColor: '#e2e8f0',
                              transform: 'translateY(-0.5px)',
                            }
                          : {},
                      }}
                    >
                      {proximo.esLocal ? (
                        <Home sx={{ fontSize: '0.82rem', color: '#64748b', flexShrink: 0 }} />
                      ) : (
                        <Flight sx={{ fontSize: '0.82rem', color: '#64748b', flexShrink: 0 }} />
                      )}
                      <span>{`${fechaInfo.diaSem} ${fechaInfo.fecha}${fechaInfo.hora ? ` · ${fechaInfo.hora}` : ''}`}</span>
                    </Box>
                  ) : proximo.esLocal ? (
                    <Home
                      onClick={(e) => {
                        if (proximo.idPartido) {
                          e.stopPropagation();
                          navigate(`/partido/${proximo.idPartido}`);
                        }
                      }}
                      sx={{
                        fontSize: '0.90rem',
                        color: 'text.secondary',
                        flexShrink: 0,
                        cursor: proximo.idPartido ? 'pointer' : 'default',
                      }}
                    />
                  ) : (
                    <Flight
                      onClick={(e) => {
                        if (proximo.idPartido) {
                          e.stopPropagation();
                          navigate(`/partido/${proximo.idPartido}`);
                        }
                      }}
                      sx={{
                        fontSize: '0.90rem',
                        color: 'text.secondary',
                        flexShrink: 0,
                        cursor: proximo.idPartido ? 'pointer' : 'default',
                      }}
                    />
                  )}
                  <TypographyApp
                    onClick={(e) => {
                      if (proximo.idRival) {
                        e.stopPropagation();
                        navigate(`/equipo/${proximo.idRival}`);
                      }
                    }}
                    sx={{
                      fontSize: '0.80rem',
                      color: 'text.primary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontWeight: 500,
                      cursor: proximo.idRival ? 'pointer' : 'default',
                      '&:hover': proximo.idRival
                        ? {
                            color: 'primary.main',
                            textDecoration: 'underline',
                          }
                        : {},
                    }}
                  >
                    {capitalizar(proximo.rival || '-')}
                  </TypographyApp>
                </Box>
              </Tooltip>
            </TableCellApp>
          );
        }

        // 3. Último partido disputado
        if (col.field === 'ultimoPartido') {
          const ultimo = equipo.ultimoPartido;
          if (!ultimo) {
            return (
              <TableCellApp
                key={col.field}
                sx={{
                  py: 0.75,
                  px: 0.75,
                  overflow: 'hidden',
                  borderRight,
                  ...(borderTop && { borderTop }),
                }}
              >
                <TypographyApp sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>-</TypographyApp>
              </TableCellApp>
            );
          }

          const fechaInfo = formatFechaPartido(ultimo.fecha);
          const fechaCompletaTxt = fechaInfo.completa ? ` · ${fechaInfo.completa}` : '';
          const jornadaTxt = ultimo.jornada ? ` · J${ultimo.jornada}` : '';
          const condicion = ultimo.esLocal ? 'Casa' : 'Fuera';
          const tooltipTitle = `${capitalizar(ultimo.rival || '')} (${condicion})${jornadaTxt}${fechaCompletaTxt}`;

          return (
            <TableCellApp
              key={col.field}
              sx={{
                py: 0.75,
                px: 0.75,
                overflow: 'hidden',
                borderRight,
                ...(borderTop && { borderTop }),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65, overflow: 'hidden' }}>
                <BadgeResultadoPartidoApp
                  partido={ultimo}
                  onClick={(e) => {
                    if (ultimo.idPartido) {
                      e.stopPropagation();
                      navigate(`/partido/${ultimo.idPartido}`);
                    }
                  }}
                  sx={{ width: 48, height: 23, fontSize: '0.76rem', flexShrink: 0 }}
                />
                <Tooltip title={tooltipTitle} arrow>
                  <TypographyApp
                    onClick={(e) => {
                      if (ultimo.idRival) {
                        e.stopPropagation();
                        navigate(`/equipo/${ultimo.idRival}`);
                      }
                    }}
                    sx={{
                      fontSize: '0.79rem',
                      color: 'text.primary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontWeight: 500,
                      cursor: ultimo.idRival ? 'pointer' : 'default',
                      '&:hover': ultimo.idRival
                        ? {
                            color: 'primary.main',
                            textDecoration: 'underline',
                          }
                        : {},
                    }}
                  >
                    {capitalizar(ultimo.rival || '')}
                  </TypographyApp>
                </Tooltip>
              </Box>
            </TableCellApp>
          );
        }

        // 4. Liga / División y posición en clasificación
        if (col.field === 'posicion') {
          const divisionTexto = obtenerDivisionTexto(equipo);

          if (!equipo.posicion && !divisionTexto) {
            return (
              <TableCellApp
                key={col.field}
                sx={{
                  py: 0.75,
                  px: 0.75,
                  borderRight,
                  ...(borderTop && { borderTop }),
                }}
              >
                <TypographyApp sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>-</TypographyApp>
              </TableCellApp>
            );
          }

          const tooltipTexto = `Liga: ${equipo.nombreLiga || divisionTexto || 'Competición'}${
            equipo.totalEquipos ? ` (${equipo.posicion}º de ${equipo.totalEquipos})` : ''
          }${equipo.puntos !== undefined && equipo.puntos !== null ? ` · ${equipo.puntos} pts` : ''} (Clic para ver clasificación)`;

          return (
            <TableCellApp
              key={col.field}
              sx={{
                py: 0.75,
                px: 0.75,
                overflow: 'hidden',
                borderRight,
                ...(borderTop && { borderTop }),
              }}
            >
              <Tooltip title={tooltipTexto} arrow>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    if (equipo.idLiga) {
                      navigate(`/liga/${equipo.idLiga}`);
                    }
                  }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.65,
                    cursor: equipo.idLiga ? 'pointer' : 'default',
                    overflow: 'hidden',
                    maxWidth: '100%',
                    '&:hover .nombre-division': {
                      color: 'primary.main',
                      textDecoration: 'underline',
                    },
                    '&:hover .badge-posicion': equipo.idLiga
                      ? {
                          backgroundColor: '#e2e8f0',
                          transform: 'translateY(-0.5px)',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        }
                      : {},
                  }}
                >
                  {equipo.posicion ? (
                    <Box
                      className="badge-posicion"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.35,
                        px: 0.65,
                        py: 0.2,
                        borderRadius: '5px',
                        fontSize: '0.76rem',
                        fontWeight: 600,
                        flexShrink: 0,
                        lineHeight: 1,
                        backgroundColor: '#f8fafc',
                        color: '#334155',
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.15s ease-in-out',
                      }}
                    >
                      <EmojiEvents sx={{ fontSize: '0.80rem', opacity: 0.85 }} />
                      <span>{equipo.posicion}º</span>
                    </Box>
                  ) : null}

                  {divisionTexto && (
                    <TypographyApp
                      className="nombre-division"
                      sx={{
                        fontSize: '0.79rem',
                        fontWeight: 500,
                        color: 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        transition: 'color 0.15s ease-in-out',
                      }}
                    >
                      {divisionTexto}
                    </TypographyApp>
                  )}
                </Box>
              </Tooltip>
            </TableCellApp>
          );
        }

        // 5. Partidos pendientes de valorar (Call-to-Action)
        if (col.field === 'partidosSinValorar') {
          return (
            <TableCellApp
              key={col.field}
              sx={{
                py: 0.75,
                px: 0.5,
                textAlign: 'center',
                borderRight,
                ...(borderTop && { borderTop }),
              }}
            >
              {equipo.partidosSinValorar > 0 ? (
                <Tooltip
                  title={`Hay ${equipo.partidosSinValorar} partido${equipo.partidosSinValorar === 1 ? '' : 's'} disputado${equipo.partidosSinValorar === 1 ? '' : 's'} con valoraciones pendientes. Haz clic para valorar.`}
                  arrow
                >
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/equipo/${equipo.idEquipo}`);
                    }}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '2px',
                      height: 24,
                      px: 0.85,
                      borderRadius: '5px',
                      fontSize: '0.74rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                      backgroundColor: '#fef3c7',
                      color: '#b45309',
                      border: '1px solid #fcd34d',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
                      transition: 'all 0.15s ease-in-out',
                      '&:hover': {
                        backgroundColor: '#fde68a',
                        borderColor: '#f59e0b',
                        color: '#92400e',
                        transform: 'translateY(-0.5px)',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      },
                    }}
                  >
                    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                      {equipo.partidosSinValorar} pend.
                    </Box>
                    <ChevronRight sx={{ fontSize: '0.85rem', flexShrink: 0 }} />
                  </Box>
                </Tooltip>
              ) : (
                <Tooltip title="Todas las valoraciones de partidos disputados están al día" arrow>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 22,
                      px: 0.85,
                      borderRadius: '4px',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      backgroundColor: '#dcfce7',
                      color: '#15803d',
                      border: '1px solid #86efac',
                      cursor: 'default',
                    }}
                  >
                    Al día ✓
                  </Box>
                </Tooltip>
              )}
            </TableCellApp>
          );
        }

        return (
          <TableCellApp
            key={col.field}
            sx={{
              py: 0.75,
              px: 0.5,
              borderRight,
              ...(borderTop && { borderTop }),
            }}
          >
            <TypographyApp sx={{ fontSize: '0.78rem' }}>{equipo[col.field] ?? '-'}</TypographyApp>
          </TableCellApp>
        );
      })}
    </TableRow>
  );
};

FilaEquipoClub.propTypes = {
  equipo: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  esNuevaCategoria: PropTypes.bool,
};

/**
 * Cuadro de mando ejecutivo con los equipos del club para la pantalla Home.
 *
 * @param {Array} equiposClub - Lista enriquecida de equipos del club.
 */
const EquiposClub = ({ equiposClub }) => {
  const [sort, setSort] = useState({ field: 'nombreEquipo', direction: 'asc' });
  const [mostrarNoCompeticion, setMostrarNoCompeticion] = useState(false);

  const temporadaVigente = temporadaActual();

  const equiposNormalizados = useMemo(() => {
    if (!equiposClub || equiposClub.length === 0) {
      return [];
    }
    return equiposClub.map((eq) => {
      const esTemporadaActual = eq.temporada === temporadaVigente;
      if (!esTemporadaActual) {
        return {
          ...eq,
          temporada: temporadaVigente,
          idLiga: null,
          nombreLiga: null,
          division: null,
          posicion: null,
          totalEquipos: null,
          puntos: null,
          racha: [],
          ultimoPartido: null,
          proximoPartido: null,
          partidosSinValorar: 0,
          enCompeticion: false,
        };
      }
      return {
        ...eq,
        enCompeticion: Boolean(eq.idLiga),
      };
    });
  }, [equiposClub, temporadaVigente]);

  const equiposFiltrados = useMemo(() => {
    if (!equiposNormalizados || equiposNormalizados.length === 0) {
      return [];
    }
    if (mostrarNoCompeticion) {
      return equiposNormalizados;
    }
    return equiposNormalizados.filter((eq) => eq.enCompeticion);
  }, [equiposNormalizados, mostrarNoCompeticion]);

  const sortedEquipos = useMemo(() => {
    if (!equiposFiltrados || equiposFiltrados.length === 0) {
      return [];
    }
    if (sort.field === 'categoria' || sort.field === 'nombreEquipo') {
      const ordenados = ordenarPorCategoria(equiposFiltrados);
      return sort.direction === 'desc' ? [...ordenados].reverse() : ordenados;
    }
    return ordenarLista(equiposFiltrados, sort);
  }, [equiposFiltrados, sort]);

  const esOrdenPorCategoria = sort.field === 'categoria' || sort.field === 'nombreEquipo';

  if (!equiposClub || equiposClub.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <TypographyApp variant="body2" color="text.secondary">
          No hay equipos registrados en el club.
        </TypographyApp>
      </Box>
    );
  }

  return (
    <Box>
      <TableModuleApp
        noBorder
        data={sortedEquipos}
        columns={COLUMNAS_EQUIPOS_CLUB}
        sort={sort}
        setSort={setSort}
        renderRow={(equipo, index) => {
          const catActual = equipo.categoria?.trim().toUpperCase();
          const catAnterior = sortedEquipos[index - 1]?.categoria?.trim().toUpperCase();
          const esNuevaCategoria =
            esOrdenPorCategoria && index > 0 && Boolean(catActual && catAnterior && catActual !== catAnterior);

          return (
            <FilaEquipoClub
              key={equipo.idEquipo}
              equipo={equipo}
              columns={COLUMNAS_EQUIPOS_CLUB}
              esNuevaCategoria={esNuevaCategoria}
            />
          );
        }}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          pt: 1.25,
          pb: 0.5,
          px: 1,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={mostrarNoCompeticion}
              onChange={(e) => setMostrarNoCompeticion(e.target.checked)}
              color="primary"
              sx={{
                '& .MuiSwitch-switchBase': {
                  '& .MuiSwitch-thumb': {
                    border: '1px solid #94a3b8',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.15)',
                  },
                  '&.Mui-checked': {
                    '& .MuiSwitch-thumb': {
                      border: '1px solid #0f766e',
                    },
                    '& + .MuiSwitch-track': {
                      borderColor: 'primary.main',
                    },
                  },
                },
                '& .MuiSwitch-track': {
                  border: '1px solid #94a3b8',
                  backgroundColor: '#e2e8f0',
                  opacity: 1,
                },
              }}
            />
          }
          label={
            <TypographyApp
              sx={{
                fontSize: '0.74rem',
                color: 'text.secondary',
                fontWeight: 500,
                userSelect: 'none',
                cursor: 'pointer',
              }}
            >
              Mostrar equipos que no están en competición
            </TypographyApp>
          }
          sx={{ m: 0 }}
        />
      </Box>
    </Box>
  );
};

EquiposClub.propTypes = {
  equiposClub: PropTypes.array,
};

export default EquiposClub;
